export const BUILT_IN_EXPENSE_CATEGORIES = [
  'Shipping',
  'Repairs',
  'Parts',
  'Reimbursements',
  'RentalReimbursement',
  'StorageFees',
  'Other',
] as const;

export type ExpenseCategory = (typeof BUILT_IN_EXPENSE_CATEGORIES)[number];

export const isBuiltInExpenseCategory = (category: string): category is ExpenseCategory =>
  (BUILT_IN_EXPENSE_CATEGORIES as readonly string[]).includes(category);

/** Status of an AI-imported expense awaiting human review. Absent ⇒ a normal, reviewed expense. */
export type ExpenseReviewStatus = 'processing' | 'needs_review' | 'reviewed' | 'failed';

/** A custom, user-created expense category stored in the `expenseCategories` collection. */
export interface ExpenseCategoryDoc {
  readonly id: string;
  label: string;
  key: string;
  createdBy: string;
  createdAt: Date;
}

export type ReimbursementSubcategory = 'Rental' | 'Truck payment' | 'Post-delivery repair';

/** Unpaid expenses queued for payment today or during the current calendar week. */
export type PlannedPaymentWindow = 'today' | 'this_week';

export const BUILT_IN_REPAIR_SUBCATEGORIES = [
  'Engine',
  'Transmission',
  'Engine replacement',
  'Transmission replacement',
  'Brakes',
  'Tires',
  'Anti theft',
  'Backup sensors',
  'Electrical',
  'Body',
  'Cameras',
  'Other',
] as const;

export type BuiltInRepairSubcategory = (typeof BUILT_IN_REPAIR_SUBCATEGORIES)[number];

/**
 * Either a built-in subcategory or a user-created custom label (Firestore-backed).
 * Stored as a plain string so dynamic subcategories work without code changes.
 */
export type RepairSubcategory = string;

/** A user-created custom repair subcategory document in Firestore (`expenseSubcategories`). */
export interface ExpenseSubcategoryDoc {
  readonly id: string;
  label: string;
  key: string;
  /** References RepairCategoryDoc.key. Null/undefined ⇒ falls under the "other" category. */
  categoryKey?: string | null;
  /** True for seeded built-ins; UI restricts delete and key edits. */
  isBuiltIn?: boolean;
  createdBy: string;
  createdAt: Date;
  updatedAt?: Date | null;
  updatedBy?: string | null;
}

/**
 * Default top-level repair categories, used to seed the `repairCategories` Firestore
 * collection on first read. Once seeded, all CRUD happens against Firestore.
 */
export const DEFAULT_REPAIR_CATEGORIES = [
  { key: 'powertrain', label: 'Powertrain', sortOrder: 10, iconKey: 'settings' },
  { key: 'suspension_steering', label: 'Suspension & Steering', sortOrder: 15, iconKey: 'spring' },
  { key: 'heating_air', label: 'Heating & Air', sortOrder: 18, iconKey: 'fan' },
  { key: 'brakes', label: 'Brakes', sortOrder: 20, iconKey: 'brake_disc' },
  { key: 'tires_wheels', label: 'Tires & Wheels', sortOrder: 30, iconKey: 'tire' },
  { key: 'electrical', label: 'Electrical & Lights', sortOrder: 40, iconKey: 'bolt' },
  { key: 'body_interior', label: 'Body & Interior', sortOrder: 50, iconKey: 'car' },
  { key: 'other', label: 'Other', sortOrder: 100, iconKey: 'other' },
] as const;

export const OTHER_REPAIR_CATEGORY_KEY = 'other';

/**
 * Stable SKU prefixes for parts catalog items, keyed by repair category key.
 * Keep prefixes stable once parts exist in a category.
 */
export const REPAIR_CATEGORY_SKU_PREFIX: Record<string, string> = {
  powertrain: 'PWT',
  suspension_steering: 'SUS',
  heating_air: 'HVA',
  brakes: 'BRK',
  tires_wheels: 'TIR',
  electrical: 'ELC',
  body_interior: 'BDY',
  other: 'OTH',
};

export const getRepairCategorySkuPrefix = (categoryKey: string): string =>
  REPAIR_CATEGORY_SKU_PREFIX[categoryKey] ?? REPAIR_CATEGORY_SKU_PREFIX[OTHER_REPAIR_CATEGORY_KEY];

/** Built-in labels keyed by repair category key (fallback when Firestore is unavailable). */
export const DEFAULT_REPAIR_CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  DEFAULT_REPAIR_CATEGORIES.map(({ key, label }) => [key, label]),
);

export const getRepairCategoryLabel = (
  categoryKey: string,
  labelsByKey?: ReadonlyMap<string, string> | Readonly<Record<string, string>>,
): string => {
  if (labelsByKey instanceof Map) {
    const fromMap = labelsByKey.get(categoryKey);
    if (fromMap) return fromMap;
  } else if (labelsByKey) {
    const record = labelsByKey as Readonly<Record<string, string>>;
    if (record[categoryKey]) return record[categoryKey];
  }
  return DEFAULT_REPAIR_CATEGORY_LABELS[categoryKey] ?? categoryKey;
};

/** Top-level repair category stored in Firestore (`repairCategories`). */
export interface RepairCategoryDoc {
  readonly id: string;
  /** Stable slug used as the join key from subcategories. Never renamed. */
  key: string;
  /** User-editable display label. */
  label: string;
  /** True for seeded built-ins; UI disables delete and key edit. */
  isBuiltIn: boolean;
  sortOrder: number;
  /** Icon key from REPAIR_CATEGORY_ICON_KEYS; null/missing falls back to a default. */
  iconKey?: string | null;
  createdAt: Date;
  createdBy: string;
  updatedAt?: Date | null;
  updatedBy?: string | null;
}

/**
 * Curated icon keys available for categories. The actual React icon is resolved
 * client-side from this key. Keep strings stable — they are persisted on Firestore docs.
 */
export const REPAIR_CATEGORY_ICON_KEYS = [
  'settings',
  'brake_disc',
  'tire',
  'bolt',
  'car',
  'other',
  'build',
  'engine',
  'oil',
  'battery',
  'lightbulb',
  'fan',
  'thermostat',
  'spring',
  'shield',
  'camera',
  'lock',
  'gas',
  'water',
  'truck',
] as const;

export type RepairCategoryIconKey = (typeof REPAIR_CATEGORY_ICON_KEYS)[number];

/** Default category for each built-in repair subcategory. */
export const DEFAULT_SUBCATEGORY_TO_CATEGORY_KEY: Record<BuiltInRepairSubcategory, string> = {
  Engine: 'powertrain',
  'Engine replacement': 'powertrain',
  Transmission: 'powertrain',
  'Transmission replacement': 'powertrain',
  Brakes: 'brakes',
  Tires: 'tires_wheels',
  Electrical: 'electrical',
  'Anti theft': 'electrical',
  'Backup sensors': 'electrical',
  Cameras: 'electrical',
  Body: 'body_interior',
  Other: 'other',
};

/**
 * Resolve a subcategory label (built-in or custom) to its top-level repair category key.
 * Falls back to `'other'` if the label is unknown or unmapped.
 */
export const getRepairCategoryKey = (
  subcategoryLabel: string,
  customSubcategories: ReadonlyArray<Pick<ExpenseSubcategoryDoc, 'label' | 'categoryKey'>>,
): string => {
  const builtIn = BUILT_IN_REPAIR_SUBCATEGORIES.find(s => s === subcategoryLabel);
  if (builtIn) return DEFAULT_SUBCATEGORY_TO_CATEGORY_KEY[builtIn];
  const custom = customSubcategories.find(s => s.label === subcategoryLabel);
  if (custom?.categoryKey) return custom.categoryKey;
  return OTHER_REPAIR_CATEGORY_KEY;
};

/** Editable per-line-item breakdown for AI-imported expenses. */
export interface ExpenseLineItem {
  description: string;
  amount: number | null;
  /** Count / units from the invoice (e.g. quarts, pads, labor hours). */
  quantity?: number | null;
  subcategory: string | null; // null = unassigned
  repairGroup?: string | null; // original repair_group label from the invoice, for reference
}

export interface Expense {
  readonly id: string;
  /**
   * A built-in ExpenseCategory or a user-created custom category label.
   * Built-in categories drive special form fields and vehicle-cost aggregation;
   * custom categories behave generically (like "Other").
   */
  category: string;
  amount: number;
  /** Whether the expense has been paid */
  paid: boolean;
  /** When the expense was marked paid (null if unpaid or not yet set) */
  paidAt: Date | null;
  /** Payment queue: show in Pay Today / Pay This Week sections until paid or cleared. */
  plannedPaymentWindow?: PlannedPaymentWindow | null;
  /** Amount charged for cc processing */
  creditCardCharge: number | null;
  /**
   * Discount applied to the pre-CC subtotal before storing `amount`.
   * `percent` = 0–100 of subtotal; `amount` = fixed dollars (capped at subtotal).
   */
  discountType: 'percent' | 'amount' | null;
  /** Percent (0–100) or dollar amount depending on `discountType`. */
  discountValue: number | null;
  description: string;
  date: Date; 
  created_by: string;
  created_at: Date; 
  updated_at: Date | null; 
  updated_by: string | null;
  
  // For "Other" category, we can provide a custom value
  customCategory: string | null;
  
  // Optional link to a vehicle
  vehicleId: string | null;
  // Optional link to a transport company (Shipping category)
  transportCompanyId?: string | null;
  
  // New fields
  invoiceNumber: string | null;
  vendorCategory: string | null;
  businessName: string | null;

  // Attachment fields - attachmentPath is also used for Gemini processing
  attachmentPath: string | null;
  attachmentURL: string | null;
  attachmentPaths?: string[] | null;
  attachmentURLs?: string[] | null;
  /**
   * Display rotation (in degrees, 0/90/180/270) for each attachment, parallel to
   * `attachmentURLs`. Persisted so a user's rotation choice in the viewer is remembered.
   */
  attachmentRotations?: number[] | null;

  // Repair subcategories (only for Repairs category)
  repairSubcategories: RepairSubcategory[] | null;
  
  // Custom repair subcategory name (when "Other" is selected)
  customRepairSubcategory: string | null;

  /**
   * Editable per-line-item breakdown enriched with subcategory assignments,
   * populated by the AI-import listener. The raw extraction stays immutable on
   * `extractedData`; this is what the review UI reads from and writes to.
   */
  lineItems?: ExpenseLineItem[] | null;
  
  // Repair amounts (only for Repairs category)
  repairPartsAmount: number | null;
  repairLaborAmount: number | null;
  repairFeesAmount: number | null;
  repairTaxAmount: number | null;
  
  // Optional link to a repair shop (only for Repairs category)
  repairShopId: string | null;
  /**
   * Optional link to the specific repair visit (repair_trucks doc) for this expense.
   * Used to surface the visit's pre-repair damage photos alongside the invoice.
   */
  repairTruckId?: string | null;
  /** Original repair timeline invoice Storage path (dedup key for repair-shop imports). */
  sourceRepairInvoiceStoragePath?: string | null;
  /** Repair visit the imported invoice came from. */
  sourceRepairTruckId?: string | null;
  // Selected part name (only for Parts category)
  partName?: string | null;
  // Reimbursement subcategory (only for Reimbursements category)
  reimbursementSubcategory?: ReimbursementSubcategory | null;

  // --- AI invoice import / review ---
  /** Review state for AI-imported invoices. Absent/null ⇒ a normal, reviewed expense. */
  reviewStatus?: ExpenseReviewStatus | null;
  /** Trigger guard: true while the extraction function should process this row. Cleared after extraction. */
  awaitingExtraction?: boolean | null;
  /** Raw structured payload returned by the extraction model, kept for audit. */
  extractedData?: Record<string, unknown> | null;
  /** Model id used for extraction (e.g. "gemini-3-pro-preview"). */
  extractionModel?: string | null;
  /** Error message if extraction failed. */
  extractionError?: string | null;
  /** Category label proposed by the model when it did not map to a built-in category. */
  suggestedCategory?: string | null;
}

/**
 * Rich structured payload extracted from a repair-shop invoice by the AI model.
 * Mirrors the extraction prompt schema. Stored verbatim on Expense.extractedData.
 */
export interface RichInvoiceLineItem {
  repair_group?: string | null;
  type?: string | null;
  description?: string | null;
  quantity?: string | null;
  labor_hours?: string | null;
  unit_price?: string | null;
  amount?: string | null;
  is_taxable?: boolean | null;
  raw_codes?: Record<string, unknown>;
  /** Subcategory assigned server-side from repair_group, surfaced via Expense.lineItems. */
  subcategory?: string | null;
}

export interface RichInvoiceTotals {
  subtotals?: Record<string, string>;
  shop_supplies?: string | null;
  epa_fee?: string | null;
  hazardous_waste_fee?: string | null;
  other_fees?: Array<{ description?: string | null; amount?: string | null; is_taxable?: boolean | null }>;
  discounts?: Array<{ description?: string | null; amount?: string | null }>;
  tax_location?: string | null;
  tax_rate?: string | null;
  tax_amount?: string | null;
  subcontracts_total?: string | null;
  grand_total?: string | null;
  amount_paid?: string | null;
  balance_due?: string | null;
  currency?: string | null;
}

export interface RichExtractedInvoice {
  invoice_header?: {
    invoice_number?: string | null;
    invoice_date?: string | null;
    due_date?: string | null;
    created_date?: string | null;
    invoiced_date?: string | null;
    payment_terms?: string | null;
    service_writer?: string | null;
    authorizer?: string | null;
    reference_numbers?: Record<string, unknown>;
  };
  vendor?: {
    name?: string | null;
    address?: string | null;
    phone?: string | null;
    fax?: string | null;
    email?: string | null;
    website?: string | null;
    tax_id?: string | null;
    state_registration?: string | null;
    platform?: string | null;
  };
  customer?: {
    name?: string | null;
    address?: string | null;
    phone?: string | null;
    email?: string | null;
    account_number?: string | null;
  };
  vehicle?: {
    vin?: string | null;
    fleet_number?: string | null;
    serial_number?: string | null;
    unit_number?: string | null;
    year?: string | null;
    make?: string | null;
    model?: string | null;
    license_plate?: string | null;
    state?: string | null;
    mileage_in?: string | null;
    mileage_out?: string | null;
    engine?: string | null;
    color?: string | null;
  };
  work_order?: Record<string, unknown> | null;
  inspection_findings?: string | null;
  line_items?: RichInvoiceLineItem[];
  totals?: RichInvoiceTotals;
  payments?: Array<Record<string, unknown>>;
  invoice_type?: string | null;
  expense_category?: string | null;
  raw_fields?: Record<string, unknown>;
  notes?: string | null;
}